import React, { useState } from "react";
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  Image,
  TextInput,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import AppColors from "../../Constants/AppColors";
import AppFonts from "../../Constants/AppFonts";
import AppImages from "../../Constants/AppImages";
import useAppNavigation from "../../Routes/useAppNavigation";
import PersonForm from "../Personnel/PersonForm";
import PersonView from "../Personnel/PersonView";
import ListScreen from "../common/ListScreen";

/* =======================================================================
 * Remaining production screens (Dispatch, Trips & Stops, Reports,
 * Communication, Support, MarketPlace/Auction, Settings) built on the
 * reusable Form / View / List components so route names mirror the site.
 * ===================================================================== */

const F = (title, fields, submitLabel) => () =>
  <PersonForm title={title} fields={fields} submitLabel={submitLabel || "Save"} />;
const V = (title, name, cards) => () => <PersonView title={title} name={name} cards={cards} />;
const L = (title, search, items, addTo) => () =>
  <ListScreen title={title} search={search} items={items} addTo={addTo} />;

const sampleRows = (pairs) => pairs.map(([label, value, full]) => ({ label, value, full }));

/* ---------------- Dispatch ---------------- */
const dispatchCustomerFields = [
  { label: "Customer Name", placeholder: "Enter Customer Name" },
  { label: "Contact Number", placeholder: "Enter Contact Number" },
  { label: "Email", placeholder: "Enter Email" },
  { label: "Company", placeholder: "Enter Company" },
  { label: "Address", placeholder: "Enter Address", multiline: true },
];
export const AddDispatchCustomer = F("Add Dispatch Customer", dispatchCustomerFields);
export const EditDispatchCustomer = F("Edit Dispatch Customer", dispatchCustomerFields, "Update");
export const ManualRouting = V("Manual Routing", "Route Plan", [
  { heading: "Routing", rows: sampleRows([["Trip", "Pune City Route"], ["Stops", "5"], ["Distance", "42 km"], ["ETA", "1h 20m"]]) },
]);
export const ViewOrder = V("View Order", "Order #1001", [
  { heading: "Order Details", rows: sampleRows([["Customer", "Mark Woods"], ["Status", "In Progress"], ["Items", "3"], ["Amount", "QAR 250"], ["Address", "100, Vishrantwadi, Pune", true]]) },
]);
export const DeliveryRequest = L("Delivery Request", "Search Request...", [
  { title: "Request #DR-2201", subtitle: "Mark Woods • Pending", to: "ViewOrder" },
  { title: "Request #DR-2202", subtitle: "John Doe • Accepted", to: "ViewOrder" },
]);
export const OrderConfDetails = V("Order Confirmation", "Order #1001", [
  { heading: "Confirmation", rows: sampleRows([["Order No", "1001"], ["Status", "Confirmed"], ["Driver", "John Doe"], ["Vehicle", "MH-12-5022"]]) },
]);

/* ---------------- Trips & Stops ---------------- */
const tripFields = [
  { label: "Trip Name", placeholder: "Enter Trip Name" },
  { label: "Trip Start Location", placeholder: "Enter Trip Start Location" },
  { label: "Trip End Location", placeholder: "Enter Trip End Location" },
  { label: "Trip Start Time", placeholder: "Select Start Time" },
  { label: "Trip End Time", placeholder: "Select End Time" },
  { label: "Trip Type", placeholder: "Select Trip Type" },
  { label: "Trip Category", placeholder: "Select Trip Category" },
  { label: "Trip Driver", placeholder: "Select Driver" },
  { label: "Trip Vehicle", placeholder: "Select Vehicle" },
];
export const AddDispatchTrip = F("Add Dispatch Trip", tripFields);
export const EditDispatchTrip = F("Edit Dispatch Trip", tripFields, "Update");
export const ViewDispatchTrip = V("View Dispatch Trip", "Pune City Trip", [
  { heading: "Trip Information", rows: sampleRows([["Trip Driver", "John Doe"], ["Trip Vehicle", "MH-12-5022"], ["Start Point", "Vishrantwadi"], ["End Point", "Kalas"], ["Trip Type", "Passenger"], ["Trip Category", "Scheduled"]]) },
  { heading: "Customer", rows: sampleRows([["Company", "Redbytes"], ["Contact No.", "99999 99999"], ["Email", "markwoods@gmail.com", true], ["Pick-Up Location", "Vishrantwadi", true], ["Drop Location", "Kalas", true]]) },
]);
const stopFields = [
  { label: "Bus Stop Code", placeholder: "Enter Bus Stop Code..." },
  { label: "Stop Name", placeholder: "Enter Stop Name" },
  { label: "Stop Address", placeholder: "Enter Stop Address", multiline: true },
  { label: "Latitude, Longitude", placeholder: "8.4683046, 76.9457075" },
];
export const AddStop = F("Add Stop", stopFields);
export const EditStop = F("Edit Stop", stopFields, "Update");
export const ViewStop = V("View Stop", "Stop - Vishrantwadi", [
  { heading: "Stop Information", rows: sampleRows([["Stop Code", "BS-101"], ["Stop Name", "Vishrantwadi"], ["Latitude", "8.4683046"], ["Longitude", "76.9457075"], ["Address", "Kalas Road, Pune", true]]) },
]);
export const AddStopnew = AddStop;
export const EditStopnew = EditStop;
export const ViewStopnew = ViewStop;

/* ---------------- Payment / Fine / Fuel / Inspection / Alerts ---------------- */
export const InvoiceDetails = V("Invoice Details", "Invoice #INV-2201", [
  { heading: "Invoice", rows: sampleRows([["Invoice No", "INV-2201"], ["Date", "08-10-2022"], ["Amount", "QAR 250"], ["Status", "Paid"], ["Customer", "Mark Woods", true]]) },
]);
export const AddVehicleFine = F("Add Vehicle Fine", [
  { label: "Vehicle Name", placeholder: "Enter Your vehicle Name" },
  { label: "Amount", placeholder: "Enter Amount" },
  { label: "Status", placeholder: "Enter Status" },
  { label: "Reason", placeholder: "Type Your Reason", multiline: true },
  { label: "Document", placeholder: "Example Document-Invoice.pdf" },
]);
export const FuelAlerts = L("Fuel Alerts", "Search Alert...", [
  { title: "Low Fuel - MH-12-5022", subtitle: "Fuel below 15% • Today 10:24" },
  { title: "Refuel - MH-12-2023", subtitle: "Refuelled 40L • Today 08:10" },
]);
export const InspectionPDF = V("Inspection Report", "Inspection #INS-44", [
  { heading: "Inspection", rows: sampleRows([["Vehicle", "MH-12-5022"], ["Inspector", "John Doe"], ["Date", "08-10-2022"], ["Result", "Passed"]]) },
]);
export const UserAlert = L("User Alerts", "Search Alert...", [
  { title: "Overspeed Alert", subtitle: "MH-12-5022 crossed 80 km/h • 10:24" },
  { title: "Geofence Exit", subtitle: "MH-12-2023 left Pune zone • 09:50" },
]);

/* ---------------- Reports ---------------- */
export const Reports = L("Reports", "Search Report...", [
  { title: "Vehicle Location Report", subtitle: "Daily • PDF / Excel", to: "ReportView" },
  { title: "Fuel Usage Report", subtitle: "Weekly • PDF / Excel", to: "ReportView" },
  { title: "Driver Behaviour Report", subtitle: "Monthly • PDF / Excel", to: "ReportView" },
  { title: "Trip Summary Report", subtitle: "Custom • PDF / Excel", to: "ReportView" },
], "ScheduleReport");
export const ScheduleReport = F("Schedule Report", [
  { label: "Report", placeholder: "Select Report" },
  { label: "Users", placeholder: "Select Users..." },
  { label: "Frequency", placeholder: "Day / Week / Month" },
  { label: "Fuel Type", placeholder: "Select Fuel Type..." },
  { label: "Area", placeholder: "Enter Area.." },
], "Schedule");
export const ReportView = V("Report View", "Vehicle Location Report", [
  { heading: "Summary", rows: sampleRows([["Vehicles", "12"], ["Distance", "842 km"], ["Trips", "36"], ["Period", "01-10 to 08-10"]]) },
]);
export const Temprature = L("Temperature", "Search Vehicle...", [
  { title: "MH-12-5022", subtitle: "4°C • Normal" },
  { title: "MH-12-2023", subtitle: "7°C • Normal" },
]);

/* ---------------- Communication ---------------- */
export const ComposeEmail = F("Compose Email", [
  { label: "Vehicle", placeholder: "Select vehicle..." },
  { label: "Driver", placeholder: "Select Driver..." },
  { label: "Trip", placeholder: "Select Trip..." },
  { label: "Subject", placeholder: "Enter Subject here..." },
  { label: "Message", placeholder: "Enter your message here...", multiline: true },
], "Send");
export const AddPushNotification = F("Push Notification", [
  { label: "Trip", placeholder: "Select Trip..." },
  { label: "Message", placeholder: "Enter your message here...", multiline: true },
], "Send");
export const NewTeamMessage = F("New Team Message", [
  { label: "Team", placeholder: "Select Team" },
  { label: "Message", placeholder: "Enter your message here...", multiline: true },
], "Send");
export const EmailSupport = F("Email Support", [
  { label: "Subject", placeholder: "Enter Subject" },
  { label: "Message", placeholder: "Enter your message here...", multiline: true },
], "Send");
export const EditAudio = F("Edit Audio", [
  { label: "Audio Name", placeholder: "Enter Audio Name" },
  { label: "Audio File", placeholder: "Select Audio File" },
], "Update");

/* ---------------- Support ---------------- */
export const CustomerSupport = L("Customer Support", "Search Ticket...", [
  { title: "Ticket #T-3301", subtitle: "App not loading • Open", to: "ViewReport" },
  { title: "Ticket #T-3302", subtitle: "GPS delay • Resolved", to: "ViewReport" },
], "ReportIssue");
export const ReportIssue = F("Report Issue", [
  { label: "Issue Name", placeholder: "Enter name of the issue..." },
  { label: "Message", placeholder: "Enter your message here...", multiline: true },
], "Submit");
export const ViewReport = V("View Report", "Ticket #T-3301", [
  { heading: "Issue", rows: sampleRows([["Issue", "App not loading"], ["Status", "Open"], ["Raised", "08-10-2022"], ["Message", "The dashboard does not load on mobile.", true]]) },
]);

/* ---------------- MarketPlace / Auction ---------------- */
export const OnlineAuction = L("Online Auction", "Search Auction...", [
  { title: "Toyota Innova - 2021", subtitle: "Current Bid: QAR 45,000", to: "BidAuctionDetails" },
  { title: "Honda City - 2020", subtitle: "Current Bid: QAR 32,000", to: "BidAuctionDetails" },
], "AuctionAddVehicle");
export const BidAuctionDetails = V("Bid Details", "Toyota Innova - 2021", [
  { heading: "Auction", rows: sampleRows([["Base Price", "QAR 40,000"], ["Current Bid", "QAR 45,000"], ["Bidders", "8"], ["Ends In", "2d 4h"]]) },
]);
export const AuctionAddVehicle = F("Add Auction Vehicle", [
  { label: "Customer Name", placeholder: "Enter Customer Name..." },
  { label: "Vehicle Name", placeholder: "Enter Vehicle Name" },
  { label: "Base Price", placeholder: "Enter Base Price" },
  { label: "Description", placeholder: "Enter Description", multiline: true },
]);
export const AuctionOfferDetails = V("Offer Details", "Offer #OF-12", [
  { heading: "Offer", rows: sampleRows([["Vehicle", "Toyota Innova"], ["Offer", "QAR 44,000"], ["By", "Mark Woods"], ["Status", "Pending"]]) },
]);
export const AuctionReferenceDetails = V("Reference Details", "Ref #RF-12", [
  { heading: "Reference", rows: sampleRows([["Vehicle", "Toyota Innova"], ["Reference", "RF-12"], ["Verified", "Yes"]]) },
]);
export const LuxuryCars = L("Luxury Cars", "Search Car...", [
  { title: "Mercedes S-Class", subtitle: "QAR 320,000", to: "BidAuctionDetails" },
  { title: "BMW 7 Series", subtitle: "QAR 290,000", to: "BidAuctionDetails" },
]);

/* ---------------- Settings / Misc ---------------- */
export const LogoUpdate = F("Update Logo", [{ label: "Company Logo", placeholder: "Select Logo Image" }], "Update");
export const Changelogo = LogoUpdate;
export const LogChanges = L("Log Changes", "Search Log...", [
  { title: "Logo updated", subtitle: "by Admin • 08-10-2022 10:24" },
  { title: "Role created: Operator", subtitle: "by Admin • 07-10-2022 16:10" },
]);
export const AddPushNotificationSettings = AddPushNotification;
export const RegistrationLocation = F("Set Your Location", [
  { label: "Address", placeholder: "Enter Address..." },
  { label: "City / State", placeholder: "Enter City / State..." },
  { label: "Country", placeholder: "Enter Country..." },
  { label: "Landmark", placeholder: "Enter Landmark..." },
], "Continue");
export const LoginWithOTP = F("Login with OTP", [
  { label: "Email / Mobile Number", placeholder: "Enter Email / Mobile Number" },
  { label: "OTP", placeholder: "Enter OTP" },
], "Verify");

export const Animation = () => (
  <SafeAreaView style={st.center}>
    <Text style={st.bigBrown}>Vehicle Tracking</Text>
    <Text style={st.sub}>Loading…</Text>
  </SafeAreaView>
);

export const Privacypolicy = () => {
  const navigation = useAppNavigation();
  return (
    <SafeAreaView style={st.safe}>
      <View style={st.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={AppImages.Backbtn} style={st.back} resizeMode="contain" />
        </TouchableOpacity>
        <Text style={st.headerTitle}>Privacy Policy</Text>
      </View>
      <ScrollView contentContainerStyle={{ padding: 20 }} showsVerticalScrollIndicator={false}>
        {["Information We Collect", "How We Use Information", "Data Security", "Your Rights", "Contact Us"].map((h, i) => (
          <View key={i} style={{ marginBottom: 18 }}>
            <Text style={st.policyHead}>{h}</Text>
            <Text style={st.policyBody}>
              We collect and process vehicle, location and account information to provide the
              vehicle tracking service. Your data is stored securely and is never shared with
              third parties without consent. You may request access or deletion at any time.
            </Text>
          </View>
        ))}
      </ScrollView>
    </SafeAreaView>
  );
};

export const LockScreen = () => {
  const navigation = useAppNavigation();
  const [pin, setPin] = useState("");
  return (
    <SafeAreaView style={st.center}>
      <Image source={AppImages.profilePic} style={st.lockAvatar} resizeMode="cover" />
      <Text style={st.lockName}>Mark Woods</Text>
      <Text style={st.sub}>Enter your password to unlock</Text>
      <TextInput
        value={pin}
        onChangeText={setPin}
        secureTextEntry
        placeholder="Password"
        placeholderTextColor="#ACACAC"
        style={st.lockInput}
      />
      <TouchableOpacity style={st.lockBtn} onPress={() => navigation.navigate("DrawerScreen")}>
        <Text style={st.lockBtnText}>Unlock</Text>
      </TouchableOpacity>
    </SafeAreaView>
  );
};

export const ChatBox = () => {
  const navigation = useAppNavigation();
  const [msg, setMsg] = useState("");
  const messages = [
    { me: false, text: "Hi, where is my vehicle now?" },
    { me: true, text: "It's near Vishrantwadi, arriving in 10 min." },
    { me: false, text: "Great, thank you!" },
  ];
  return (
    <SafeAreaView style={st.safe}>
      <View style={st.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={AppImages.Backbtn} style={st.back} resizeMode="contain" />
        </TouchableOpacity>
        <Text style={st.headerTitle}>Chat</Text>
      </View>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
        {messages.map((m, i) => (
          <View key={i} style={[st.bubble, m.me ? st.bubbleMe : st.bubbleThem]}>
            <Text style={[st.bubbleText, m.me && { color: AppColors.white }]}>{m.text}</Text>
          </View>
        ))}
      </ScrollView>
      <View style={st.chatBar}>
        <TextInput
          value={msg}
          onChangeText={setMsg}
          placeholder="Type a message..."
          placeholderTextColor="#ACACAC"
          style={st.chatInput}
        />
        <TouchableOpacity style={st.sendBtn} onPress={() => setMsg("")}>
          <Text style={st.sendText}>Send</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const st = StyleSheet.create({
  safe: { flex: 1, backgroundColor: AppColors.white },
  center: { flex: 1, backgroundColor: AppColors.white, alignItems: "center", justifyContent: "center", padding: 24 },
  header: {
    width: "100%", height: 58, flexDirection: "row", alignItems: "center", paddingHorizontal: 20,
    backgroundColor: AppColors.white, shadowColor: "#9C4900", shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 1, shadowRadius: 10, elevation: 6,
  },
  back: { width: 8, height: 15 },
  headerTitle: { color: AppColors.brown1, fontSize: 16, fontFamily: AppFonts.OverpassMedium, marginLeft: 20 },
  bigBrown: { color: AppColors.brown1, fontSize: 26, fontFamily: AppFonts.OverpassBold },
  sub: { color: AppColors.dualbrown, fontSize: 14, fontFamily: AppFonts.OverpassRegular, marginTop: 8 },
  policyHead: { color: AppColors.brown1, fontSize: 15, fontFamily: AppFonts.OverpassBold, marginBottom: 6 },
  policyBody: { color: AppColors.dualbrown, fontSize: 13, fontFamily: AppFonts.OverpassRegular, lineHeight: 20 },
  lockAvatar: { width: 90, height: 90, borderRadius: 45, backgroundColor: AppColors.sandal },
  lockName: { color: AppColors.brown1, fontSize: 18, fontFamily: AppFonts.OverpassBold, marginTop: 14 },
  lockInput: {
    width: "100%", borderWidth: 1, borderColor: "#ACACAC", borderRadius: 8, paddingHorizontal: 12,
    paddingVertical: 10, marginTop: 24, fontFamily: AppFonts.OverpassRegular, color: AppColors.brown1,
  },
  lockBtn: { width: "100%", backgroundColor: AppColors.brown1, borderRadius: 10, paddingVertical: 12, alignItems: "center", marginTop: 16 },
  lockBtnText: { color: AppColors.white, fontSize: 15, fontFamily: AppFonts.OverpassMedium },
  bubble: { maxWidth: "78%", borderRadius: 12, paddingHorizontal: 14, paddingVertical: 10, marginBottom: 10 },
  bubbleMe: { backgroundColor: AppColors.brown1, alignSelf: "flex-end" },
  bubbleThem: { backgroundColor: "#F6EFE9", alignSelf: "flex-start" },
  bubbleText: { color: AppColors.brown1, fontSize: 14, fontFamily: AppFonts.OverpassRegular },
  chatBar: { flexDirection: "row", alignItems: "center", padding: 12, borderTopWidth: 1, borderTopColor: "#9C49001A" },
  chatInput: {
    flex: 1, borderWidth: 1, borderColor: "#ACACAC", borderRadius: 20, paddingHorizontal: 16,
    paddingVertical: 9, fontFamily: AppFonts.OverpassRegular, color: AppColors.brown1,
  },
  sendBtn: { marginLeft: 10, backgroundColor: AppColors.brown1, borderRadius: 20, paddingHorizontal: 18, paddingVertical: 10 },
  sendText: { color: AppColors.white, fontFamily: AppFonts.OverpassMedium },
});
